import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { staffSettingsService, StaffSetting, DayOfWeek, WeeklyStaffSetting, MonthlyStaffSetting } from '../services/staffSettingsService';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Loader2, Save, RotateCcw, Calendar, Clock, AlertCircle, ArrowDown } from 'lucide-react';

const getDaysOfWeek = (translate: (key: string, fallback?: string) => string): { id: DayOfWeek; label: string }[] => [
  { id: 'monday', label: translate('pos.settings.staff.days_of_week.monday', 'Monday') },
  { id: 'tuesday', label: translate('pos.settings.staff.days_of_week.tuesday', 'Tuesday') },
  { id: 'wednesday', label: translate('pos.settings.staff.days_of_week.wednesday', 'Wednesday') },
  { id: 'thursday', label: translate('pos.settings.staff.days_of_week.thursday', 'Thursday') },
  { id: 'friday', label: translate('pos.settings.staff.days_of_week.friday', 'Friday') },
  { id: 'saturday', label: translate('pos.settings.staff.days_of_week.saturday', 'Saturday') },
  { id: 'sunday', label: translate('pos.settings.staff.days_of_week.sunday', 'Sunday') },
];

const StaffSettings: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<StaffSetting | null>(null);

  // Local state for toggles
  const [isWeeklyEnabled, setIsWeeklyEnabled] = useState(false);
  const [isMonthlyEnabled, setIsMonthlyEnabled] = useState(false);
  
  const fetchSettings = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await staffSettingsService.getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch staff settings:', error);
      notify(translate('pos.settings.staff.fetch_failed', 'Failed to load staff settings'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify, translate]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (settings) {
      // Determine if overrides are enabled based on data presence
      setIsWeeklyEnabled(!!settings.weekly);
      setIsMonthlyEnabled(!!settings.monthly);
    }
  }, [settings]);

  const handleToggleWeekly = (enabled: boolean) => {
    setIsWeeklyEnabled(enabled);
    if (enabled && settings && (!settings.weekly || !settings.weekly.standardWeeklyHours)) {
       updateWeekly({
           standardWeeklyHours: settings.standardWeeklyHours,
           standardDailyHours: settings.standardDailyHours,
           daysOffCount: 0,
           specificDaysOff: []
       });
    }
  };

  const handleToggleMonthly = (enabled: boolean) => {
    setIsMonthlyEnabled(enabled);
    if (enabled && settings && (!settings.monthly || !settings.monthly.daysOffCount)) {
        updateMonthly({
            daysOffCount: 0
        });
    }
  };

  // ... inside StaffSettings component ...
  
  const handleSave = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      
      // Prepare updates based on enabled toggles
      const updates: Partial<StaffSetting> = {
        ...settings,
        standardDailyHours: settings.standardDailyHours, // ensure base daily hours are preserved/updated
        weekly: isWeeklyEnabled ? settings.weekly || { 
            standardWeeklyHours: settings.standardWeeklyHours,
            standardDailyHours: settings.standardDailyHours 
        } : undefined,
        monthly: isMonthlyEnabled ? { 
            daysOffCount: settings.monthly?.daysOffCount || 0,
            // Exclude hours from monthly settings as per new requirements
        } : undefined,
      };

      const updated = await staffSettingsService.updateSettings(updates);
      setSettings(updated);
      notify(translate('pos.settings.staff.save_success', 'Staff settings updated successfully'), { type: 'success' });
    } catch (error) {
      console.error('Failed to update staff settings:', error);
      notify(translate('pos.settings.staff.save_failed', 'Failed to update staff settings'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLoading(true);
    fetchSettings().then(() => setLoading(false));
  };

  // Helper to update deeply nested state
  const updateWeekly = (updates: Partial<WeeklyStaffSetting>) => {
    if (!settings) return;
    setSettings({ 
      ...settings, 
      weekly: { ...(settings.weekly || {}), ...updates } 
    });
  };

  const updateMonthly = (updates: Partial<MonthlyStaffSetting>) => {
    if (!settings) return;
    setSettings({ 
      ...settings, 
      monthly: { ...(settings.monthly || {}), ...updates } 
    });
  };

  const updateYearly = (field: string, value: any) => {
    if (!settings) return;
    
    if (field === 'daysOffCount') {
        setSettings({ 
            ...settings, 
            paidDayOffAllowance: value,
            yearly: { ...(settings.yearly || {}), daysOffCount: value } 
        });
    } else {
        setSettings({ ...settings, [field]: value });
    }
  };



  const handleDayToggle = (day: DayOfWeek) => {
    if (!settings) return;

    const currentDays = settings.weekly?.specificDaysOff || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    
    // Auto-calculate new weekly hours based on daily hours anchor
    const workingDays = Math.max(0, 7 - newDays.length);
    const dailyHours = settings.weekly?.standardDailyHours ?? settings.standardDailyHours ?? 0;

    if (dailyHours > 0) {
      const newWeeklyHours = parseFloat((dailyHours * workingDays).toFixed(2));
      updateWeekly({ 
        specificDaysOff: newDays,
        standardWeeklyHours: newWeeklyHours
      });
    } else {
      updateWeekly({ specificDaysOff: newDays });
    }
  };

  /**
   * Automatically calculate and sync Weekly/Daily hours based on implicit working days.
   * Triggered onBlur of either input.
   */
  /**
   * Automatically calculate and sync Weekly/Daily hours based on implicit working days.
   * Triggered onBlur of either input.
   */
  const handleSyncHours = (
    section: 'weekly' | 'monthly' | 'yearly',
    trigger: 'daily' | 'weekly'
  ) => {
    if (!settings) return;

    let workingDays = 5; // default fallback

    // 1. Determine Implicit Working Days
    if (section === 'weekly') {
      const weeklyData = settings.weekly || {};
      let daysOff = 0;
      
      if (Array.isArray(weeklyData.specificDaysOff)) {
        daysOff = weeklyData.specificDaysOff.length;
      } else if (typeof weeklyData.daysOffCount === 'number') {
        daysOff = weeklyData.daysOffCount;
      }
      
      workingDays = Math.max(0, 7 - daysOff);
      
    } else if (section === 'monthly') {
      // Logic adjusted: Assume standard 52 weeks.
      // If we strictly use (365 - Off) / 52, we get ~6.8 days for small allowances, effectively ignoring weekends.
      // Better heuristic for Monthly/Yearly: 
      // If current Weekly/Daily ratio exists and is reasonable (3-7), use it.
      // Otherwise, assume 5 working days as a safe baseline, adjusted slightly by allowance if it's large.
      
      const currentWeekly = settings.monthly?.standardWeeklyHours || settings.standardWeeklyHours;
      const currentDaily = settings.monthly?.standardDailyHours || settings.standardDailyHours;
      
      if (currentWeekly && currentDaily && currentDaily > 0) {
        const ratio = currentWeekly / currentDaily;
        if (ratio >= 1 && ratio <= 7) {
            workingDays = ratio;
        }
      }
      
    } else {
       // Yearly
       const currentWeekly = settings.standardWeeklyHours;
       const currentDaily = settings.standardDailyHours;
       
       if (currentWeekly && currentDaily && currentDaily > 0) {
         const ratio = currentWeekly / currentDaily;
         if (ratio >= 1 && ratio <= 7) {
             workingDays = ratio;
         }
       }
    }

    // Safety check
    if (workingDays <= 0) workingDays = 5;

    // 2. Perform Calculation & Update
    if (trigger === 'daily') {
      // Changed Daily -> Update Weekly
      let daily = 0;
      if (section === 'weekly') daily = settings.weekly?.standardDailyHours || 0;
      else if (section === 'monthly') daily = settings.monthly?.standardDailyHours || 0;
      else daily = settings.standardDailyHours || 0;

      if (daily > 0) {
        const newWeekly = parseFloat((daily * workingDays).toFixed(2));
        if (section === 'weekly') updateWeekly({ standardWeeklyHours: newWeekly });
        else if (section === 'monthly') updateMonthly({ standardWeeklyHours: newWeekly });
        else updateYearly('standardWeeklyHours', newWeekly);
      }

    } else {
      // Changed Weekly -> Update Daily
      let weekly = 0;
      if (section === 'weekly') weekly = settings.weekly?.standardWeeklyHours || 0;
      else if (section === 'monthly') weekly = settings.monthly?.standardWeeklyHours || 0;
      else weekly = settings.standardWeeklyHours || 0;

      if (weekly > 0) {
        const newDaily = parseFloat((weekly / workingDays).toFixed(2));
        if (section === 'weekly') updateWeekly({ standardDailyHours: newDaily });
        else if (section === 'monthly') updateMonthly({ standardDailyHours: newDaily });
        else updateYearly('standardDailyHours', newDaily);
      }
    }
  };

  /**
   * Calculate effective stats for display
   */
  const getEffectiveStats = () => {
    if (!settings) return { daysPerYear: 0, hoursPerWeek: 0, hoursPerDay: 0, source: 'Loading...' };

    let daysPerYear = settings.paidDayOffAllowance || 0;
    let hoursPerWeek = settings.standardWeeklyHours || 40;
    let hoursPerDay = settings.standardDailyHours || 8;
    let source = 'Base / Yearly';

    // Check Monthly (overrides base)
    if (isMonthlyEnabled && settings.monthly) {
        if (settings.monthly.daysOffCount !== undefined) {
            daysPerYear = settings.monthly.daysOffCount * 12;
            source = 'Monthly Override';
        }
        if (settings.monthly.standardWeeklyHours !== undefined) {
            hoursPerWeek = settings.monthly.standardWeeklyHours;
        }
        if (settings.monthly.standardDailyHours !== undefined) {
            hoursPerDay = settings.monthly.standardDailyHours;
        }
    }

    // Check Weekly (overrides monthly and base)
    if (isWeeklyEnabled && settings.weekly) {
        if (settings.weekly.specificDaysOff && settings.weekly.specificDaysOff.length > 0) {
            daysPerYear = settings.weekly.specificDaysOff.length * 52;
            source = 'Weekly Override';
        } else if (settings.weekly.daysOffCount !== undefined) {
            daysPerYear = settings.weekly.daysOffCount * 52;
            source = 'Weekly Override';
        }
        
        if (settings.weekly.standardWeeklyHours !== undefined) {
            hoursPerWeek = settings.weekly.standardWeeklyHours;
        }
        if (settings.weekly.standardDailyHours !== undefined) {
            hoursPerDay = settings.weekly.standardDailyHours;
        }
    }

    return { daysPerYear, hoursPerWeek, hoursPerDay, source };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!settings) return null;

  const effectiveStats = getEffectiveStats();

  return (
    <div className="space-y-6">
      {/* Effective Policy Summary */}
      <Card className="bg-muted/30 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-primary/10 rounded-full">
                <AlertCircle className="h-6 w-6 text-primary" />
            </div>
            <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {translate('pos.settings.staff.effective_policy', 'Effective Staff Policy')}
                </h3>
                <p className="text-muted-foreground mb-3">
                    {translate('pos.settings.staff.currently_using', 'Currently using')} <strong>{effectiveStats.source}</strong> {translate('pos.settings.staff.rules', 'rules')}.
                </p>
                <div className="flex gap-4 text-sm flex-wrap">
                    <div className="px-3 py-1 bg-background rounded border shadow-sm">
                        <span className="text-muted-foreground mr-2">{translate('pos.settings.staff.paid_time_off', 'Paid Time Off:')}</span>
                        <span className="font-bold">{Math.round(effectiveStats.daysPerYear)} {translate('pos.settings.staff.days_per_year', 'days/year')}</span>
                    </div>
                    <div className="px-3 py-1 bg-background rounded border shadow-sm">
                        <span className="text-muted-foreground mr-2">{translate('pos.settings.staff.weekly_load', 'Weekly Load:')}</span>
                        <span className="font-bold">{effectiveStats.hoursPerWeek} {translate('pos.settings.staff.hours_per_week', 'hours/week')}</span>
                    </div>
                     <div className="px-3 py-1 bg-background rounded border shadow-sm">
                        <span className="text-muted-foreground mr-2">{translate('pos.settings.staff.daily_load', 'Daily Load:')}</span>
                        <span className="font-bold">{effectiveStats.hoursPerDay} {translate('pos.settings.staff.hours_per_day', 'hours/day')}</span>
                    </div>
                </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
             <div className="space-y-1">
                <CardTitle>{translate('pos.settings.staff.configuration_hierarchy', 'Configuration Hierarchy')}</CardTitle>
                <CardDescription>
                    {translate('pos.settings.staff.hierarchy_description', 'Settings are applied in order of priority: Weekly → Monthly → Yearly. Enabling a higher priority setting will override lower ones.')}
                </CardDescription>
             </div>
             <div className="flex gap-2">
                <Button variant="outline" onClick={handleReset} disabled={saving}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    {translate('pos.common.reset', 'Reset Changes')}
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    {translate('pos.common.save', 'Save Configuration')}
                </Button>
             </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-0">
            
            {/* 1. Weekly Settings */}
            <div className={`relative p-6 rounded-lg border-2 transition-all ${isWeeklyEnabled ? 'border-primary bg-primary/5' : 'border-border border-dashed opacity-80'}`}>
                {isWeeklyEnabled && <Badge className="absolute -top-3 left-4">{translate('pos.settings.staff.highest_priority', 'Highest Priority')}</Badge>}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-muted-foreground" />
                        <h3 className="font-semibold text-lg">{translate('pos.settings.staff.weekly_settings', 'Weekly Settings')}</h3>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Label htmlFor="weekly-mode">{translate('pos.settings.staff.enable_weekly_rules', 'Enable Weekly Rules')}</Label>
                        <Switch id="weekly-mode" checked={isWeeklyEnabled} onCheckedChange={handleToggleWeekly} />
                    </div>
                </div>

                {isWeeklyEnabled && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-top-2 duration-200">
                        {/* Days Off Logic */}
                        <div className="space-y-3">
                            <Label>{translate('pos.settings.staff.specific_days_off', 'Specific Days Off')}</Label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {getDaysOfWeek(translate).map((day) => (
                                <div key={day.id} className="flex items-center space-x-2">
                                    <Checkbox 
                                    id={`day-${day.id}`} 
                                    checked={(settings.weekly?.specificDaysOff || []).includes(day.id)}
                                    onCheckedChange={() => handleDayToggle(day.id)}
                                    />
                                    <Label htmlFor={`day-${day.id}`} className="cursor-pointer font-normal">
                                    {day.label}
                                    </Label>
                                </div>
                                ))}
                            </div>
                        </div>

                        {(!settings.weekly?.specificDaysOff || settings.weekly.specificDaysOff.length === 0) && (
                            <div className="grid md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>{translate('pos.settings.staff.days_off_count_per_week', 'Or Days Off Count (Per Week)')}</Label>
                                    <Input 
                                        type="number" 
                                        min={0} max={7} 
                                        value={settings.weekly?.daysOffCount || 0}
                                        onChange={(e) => {
                                          const val = parseFloat(e.target.value) || 0;
                                          const workingDays = Math.max(0, 7 - val);
                                          const dailyHours = settings.weekly?.standardDailyHours ?? settings.standardDailyHours ?? 0;
                                          
                                          if (dailyHours > 0) {
                                            updateWeekly({ 
                                              daysOffCount: val,
                                              standardWeeklyHours: parseFloat((dailyHours * workingDays).toFixed(2))
                                            });
                                          } else {
                                            updateWeekly({ daysOffCount: val });
                                          }
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>{translate('pos.settings.staff.standard_weekly_hours', 'Standard Weekly Hours')}</Label>
                                <Input 
                                    type="number" 
                                    value={settings.weekly?.standardWeeklyHours ?? settings.standardWeeklyHours}
                                    onChange={(e) => updateWeekly({ standardWeeklyHours: parseFloat(e.target.value) || 0 })}
                                    onBlur={() => handleSyncHours('weekly', 'weekly')}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>{translate('pos.settings.staff.standard_daily_hours', 'Standard Daily Hours')}</Label>
                                <Input 
                                    type="number" 
                                    value={settings.weekly?.standardDailyHours ?? settings.standardDailyHours}
                                    onChange={(e) => updateWeekly({ standardDailyHours: parseFloat(e.target.value) || 0 })}
                                    onBlur={() => handleSyncHours('weekly', 'daily')}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Arrow Connector */}
            <div className="flex justify-center -my-2 relative z-10">
                <ArrowDown className="text-muted-foreground h-6 w-6 bg-background rounded-full p-1 border" />
            </div>

            {/* 2. Monthly Settings */}
            <div className={`relative p-6 rounded-lg border-2 transition-all mt-4 ${isMonthlyEnabled ? 'border-primary bg-primary/5' : 'border-border border-dashed opacity-80'}`}>
                {isMonthlyEnabled && <Badge variant="secondary" className="absolute -top-3 left-4">{translate('pos.settings.staff.overrides_policies', 'Overrides Policies')}</Badge>}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-muted-foreground" />
                        <h3 className="font-semibold text-lg">{translate('pos.settings.staff.monthly_settings', 'Monthly Settings')}</h3>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Label htmlFor="monthly-mode">{translate('pos.settings.staff.enable_monthly_rules', 'Enable Monthly Rules')}</Label>
                        <Switch id="monthly-mode" checked={isMonthlyEnabled} onCheckedChange={handleToggleMonthly} />
                    </div>
                </div>

                {isMonthlyEnabled && (
                     <div className="grid md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="space-y-2">
                            <Label>{translate('pos.settings.staff.days_off_per_month', 'Days Off per Month')}</Label>
                            <Input 
                                type="number" 
                                min={0} max={31}
                                value={settings.monthly?.daysOffCount || 0}
                                onChange={(e) => updateMonthly({ daysOffCount: parseFloat(e.target.value) || 0 })}
                            />
                            <p className="text-xs text-muted-foreground">x12 months = {Math.round((settings.monthly?.daysOffCount || 0) * 12)} {translate('pos.settings.staff.days_per_year', 'days/year')}</p>
                        </div>

                    </div>
                )}
            </div>

            {/* Arrow Connector */}
            <div className="flex justify-center -my-2 relative z-10">
                <ArrowDown className="text-muted-foreground h-6 w-6 bg-background rounded-full p-1 border" />
            </div>

            {/* 3. Policies */}
            <div className="p-6 rounded-lg border mt-4 bg-card">
                <div className="flex items-center gap-2 mb-4">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <h3 className="font-semibold text-lg">{translate('pos.settings.staff.policies', 'Policies')}</h3>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <Label>{translate('pos.settings.staff.annual_paid_day_off_allowance', 'Annual Paid Day Off Allowance')}</Label>
                        <Input 
                            type="number"
                            min={0}
                            value={settings.paidDayOffAllowance}
                            onChange={(e) => updateYearly('daysOffCount', parseFloat(e.target.value) || 0)}
                        />
                         <p className="text-xs text-muted-foreground">{translate('pos.settings.staff.annual_allowance_hint', 'Total days per year. Used if no Weekly/Monthly rules apply.')}</p>
                    </div>



                    <div className="space-y-2">
                        <Label>{translate('pos.settings.staff.fiscal_year_start_month', 'Fiscal Year Start Month')}</Label>
                        <Select
                            value={settings.fiscalYearStartMonth.toString()}
                            onValueChange={(value) => updateYearly('fiscalYearStartMonth', parseInt(value))}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Array.from({ length: 12 }).map((_, i) => (
                                    <SelectItem key={i} value={i.toString()}>
                                    {new Date(0, i).toLocaleString('default', { month: 'long' })}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label>{translate('pos.settings.staff.reset_policy', 'Reset Policy')}</Label>
                        <Select
                            value={settings.paidDayOffWipePolicy}
                            onValueChange={(value) => updateYearly('paidDayOffWipePolicy', value)}
                        >
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="wipe_monthly">{translate('pos.settings.staff.wipe_monthly', 'Wipe Monthly (Reset every month)')}</SelectItem>
                                <SelectItem value="wipe_yearly">{translate('pos.settings.staff.wipe_yearly', 'Wipe Yearly (Reset at fiscal year end)')}</SelectItem>
                                <SelectItem value="keep">{translate('pos.settings.staff.keep_accumulate', 'Keep (Accumulate indefinitely)')}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>


        </CardContent>
      </Card>
    </div>
  );
};

export default StaffSettings;
